<template>
  <div class="modern-form-container">
    <!-- Language Selector -->
    <div class="language-selector">
      <div class="language-dropdown-container" @click="toggleLanguageDropdown">
        <div class="language-selected">
          <span class="language-flag">{{ getCurrentLanguageFlag() }}</span>
          <span class="language-name">{{ getCurrentLanguageName() }}</span>
          <svg
            class="dropdown-icon"
            :class="{rotated: showLanguageDropdown}"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M6 9L12 15L18 9"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <div v-if="showLanguageDropdown" class="language-options">
          <div
            v-for="lang in availableLanguages"
            :key="lang.code"
            class="language-option"
            :class="{active: locale === lang.code}"
            @click.stop="selectLanguage(lang.code)"
          >
            <span class="language-flag">{{ lang.flag }}</span>
            <span class="language-name">{{ lang.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Preload hint for faster loading -->
    <link
      rel="preload"
      as="document"
      href="https://player.vimeo.com/video/1050807756?background=1&autoplay=1&loop=1&muted=1&playsinline=1&preload=auto"
    />

    <!-- Video Background -->
    <div class="video-background">
      <!-- Preload image that shows immediately -->
      <img
        src="/assets/hit-online-hero-image.webp"
        alt="Hit-Online Hero Background"
        class="preload-image"
        loading="eager"
      />

      <iframe
        class="video-iframe"
        src="https://player.vimeo.com/video/1050807756?background=1&autoplay=1&loop=1&muted=1&playsinline=1&preload=auto&quality=720p"
        frameborder="0"
        allow="fullscreen"
        allowfullscreen
        loading="eager"
        @load="onVideoReady"
      />
      <div class="video-overlay" />
    </div>

    <div class="center-wrapper">
      <!-- Main Content Card -->
      <div class="logo-container">
        <img
          src="/assets/hit_online_logo_full.svg"
          alt="Hit-Online Logo"
          class="hit-logo"
          height="150"
          width="300"
        />
      </div>

      <div class="form-card">
        <div class="card-header">
          <h2 class="form-title">
            {{ t('admin-ui.register_email_form_explanation_title_1') }}
          </h2>
          <p class="form-subtitle">{{ t('admin-ui.quote') }}</p>
        </div>

        <div class="card-body">
          <hit-form
            name="RegisterEmailForm"
            :value="objectFirstFormData"
            :validations="{}"
            :force-cancel-button-hidden="true"
            :force-save-button-hidden="true"
          >
            <template
              #default="{
                formData,
              }: {
                formData: {
                  email: string | null;
                };
              }"
            >
              <div
                class="email-input-container"
                @keydown.enter.prevent.stop="onEmailEnterKey"
              >
                <hit-input
                  name="email"
                  :value="formData.email || ''"
                  :label="t('admin-ui.email')"
                  :placeholder="'nom@entreprise.com'"
                  @input="onChangeMail"
                />
              </div>

              <div class="button-container">
                <hit-button
                  color="accent"
                  :label="t('admin-ui.lets_go')"
                  suffix-icon="send"
                  icon-size="sm"
                  class="send-button"
                  :disabled="!emailCompleted"
                  @click="$emit('sendMail', {email: objectFirstFormData.email})"
                />
              </div>
            </template>
          </hit-form>
        </div>
      </div>

      <!-- Banner -->
      <div v-if="showBanner" class="banner-container">
        <hit-banner
          :show-animation="showAnimationBanner"
          @close="closeBanner()"
        >
          <template #dynamic-content>
            <h6>{{ textBanner }}</h6>
          </template>
        </hit-banner>
      </div>
    </div>

    <footer class="register-footer">
      <p>© {{ new Date().getFullYear() }} Hit-Online. Tous droits réservés.</p>
    </footer>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import {
  HitButton,
  HitForm,
  HitGroupInput,
  HitInput,
  HitPanel,
  HitSelect,
} from '@hit/components/src';
import {useI18n} from 'vue-i18n';
import {Validations} from '../tools/Validations';
import TopBarNavigation from '../TopBarNavigation.vue';
import HitBanner from '@hit/components/src/components/banner/HitBanner.vue';

export default defineComponent({
  name: 'RegisterEmailForm',
  components: {
    TopBarNavigation,
    HitPanel,
    HitSelect,
    HitInput,
    HitGroupInput,
    HitButton,
    HitForm,
    HitBanner,
  },
  props: {
    showMobileView: {
      type: Boolean,
      default: false,
    },
    showBanner: {
      type: Boolean,
      default: false,
      required: true,
    },
    textBanner: {
      type: String,
      default: '',
      required: true,
    },
    showAnimationBanner: {
      type: Boolean,
      default: false,
      required: true,
    },
  },
  setup() {
    const {t, locale} = useI18n();
    return {t, locale};
  },
  data() {
    return {
      objectFirstFormData: {
        email: null as string | null,
      },
      objectSecondFormData: {
        email: null as string | null,
        password: null as string | null,
        confirmPassword: null as string | null,
      },
      isErrorWithMailPart: true,
      errorMessageEmail: '' as string,
      isErrorWithPasswordPart: true,
      errorMessagePassword: '' as string,
      isActive: false,
      emailCompleted: false,
      mobileView: false,
      videoReady: false,
      showLanguageDropdown: false,
      availableLanguages: [
        {code: 'fr-BE', flag: '🇫🇷', name: 'Français'},
        {code: 'nl-BE', flag: '🇳🇱', name: 'Nederlands'},
        {code: 'de-BE', flag: '🇩🇪', name: 'Deutsch'},
        {code: 'en-GB', flag: '🇬🇧', name: 'English'},
        {code: 'mr-IN', flag: '🇮🇳', name: 'Marathi'},
        {code: 'hi-IN', flag: '🇮🇳', name: 'Hindi'},
      ],
    };
  },
  mounted() {
    // Restore saved language preference
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage) {
      this.locale = savedLanguage;
    }

    // Close language dropdown when clicking outside
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const languageSelector = document.querySelector('.language-selector');
      if (languageSelector && !languageSelector.contains(target)) {
        this.showLanguageDropdown = false;
      }
    });
  },
  watch: {
    locale() {
      // Update banner text when language changes
      if (this.showBanner) {
        // Emit event to parent to refresh banner text
        this.$emit('languageChanged');
      }
    },
  },
  methods: {
    allowClickOnSendMail(): boolean {
      if (this.objectFirstFormData.email !== null) {
        if (Validations.validateEmail(this.objectFirstFormData.email)) {
          this.emailCompleted = true;
          return true;
        } else {
          this.emailCompleted = false;
          return false;
        }
      } else {
        this.emailCompleted = false;
        return false;
      }
    },
    closeBanner() {
      this.$emit('close');
    },
    onChangeMail(value: any) {
      this.objectFirstFormData.email = value;
      this.allowClickOnSendMail();
    },
    onEmailEnterKey() {
      if (this.allowClickOnSendMail()) {
        this.$emit('sendMail', {email: this.objectFirstFormData.email});
      }
    },
    toggleLanguageDropdown() {
      this.showLanguageDropdown = !this.showLanguageDropdown;
    },
    selectLanguage(code: string) {
      this.locale = code;
      localStorage.setItem('selectedLanguage', code);
      this.showLanguageDropdown = false;
    },
    getCurrentLanguageFlag() {
      const lang = this.availableLanguages.find((l) => l.code === this.locale);
      return lang ? lang.flag : '🌐';
    },
    getCurrentLanguageName() {
      const lang = this.availableLanguages.find((l) => l.code === this.locale);
      return lang ? lang.name : 'Language';
    },
    onVideoReady() {
      // Wait a bit more to ensure video is really ready to play
      setTimeout(() => {
        this.videoReady = true;
        const preloadImage = document.querySelector(
          '.preload-image',
        ) as HTMLElement;
        const videoIframe = document.querySelector(
          '.video-iframe',
        ) as HTMLElement;
        if (preloadImage && videoIframe) {
          preloadImage.style.opacity = '0';
          videoIframe.style.opacity = '1';
        }
      }, 1000); // Give video 1 second to fully initialize
    },
  },
});
</script>

<style scoped>
.modern-form-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem 1rem;
  position: relative;
  overflow: hidden;
  background: #000; /* Simple black background instead of blue gradient */
}

.center-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

@media (min-width: 1024px) {
  .center-wrapper {
    transform: translateY(-8vh);
  }
}

.language-selector {
  position: fixed;
  top: 1rem;
  right: 1rem;
  z-index: 10;
}

.language-dropdown-container {
  position: relative;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #0d3749;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(20px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  min-width: 140px;
}

.language-dropdown-container:hover {
  background: rgba(255, 255, 255, 1);
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  border-color: rgba(0, 153, 124, 0.3);
}

.language-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.language-flag {
  font-size: 1.1em;
}

.language-name {
  font-weight: 500;
  white-space: nowrap;
}

.dropdown-icon {
  transition: transform 0.3s ease;
  color: #6b7280;
}

.dropdown-icon.rotated {
  transform: rotate(180deg);
}

.language-options {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0.75rem;
  padding: 0.5rem 0;
  backdrop-filter: blur(20px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  animation: slideDown 0.2s ease-out;
}

.language-option {
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.language-option:hover {
  background: linear-gradient(
    135deg,
    rgba(0, 153, 124, 0.1),
    rgba(26, 188, 156, 0.1)
  );
}

.language-option.active {
  background: linear-gradient(
    135deg,
    rgba(0, 153, 124, 0.15),
    rgba(26, 188, 156, 0.15)
  );
  color: #00997c;
  font-weight: 600;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.video-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.preload-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 1;
  transition: opacity 1s ease-in-out;
  z-index: 0;
}

.video-iframe {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100vw;
  height: 56.25vw; /* 16:9 aspect ratio */
  min-height: 100vh;
  min-width: 177.78vh; /* 16:9 aspect ratio */
  transform: translate(-50%, -50%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 1s ease-in-out;
  z-index: 1;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(13, 55, 73, 0.75) 0%,
    rgba(26, 77, 102, 0.6) 50%,
    rgba(13, 55, 73, 0.75) 100%
  );
  backdrop-filter: blur(1px);
  z-index: 1;
}

.logo-container {
  position: relative;
  z-index: 3;
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}

.hit-logo {
  max-width: 300px;
  height: auto;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.form-card {
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  padding: 0;
  max-width: 500px;
  width: 100%;
  overflow: hidden;
  backdrop-filter: blur(20px);
  position: relative;
  z-index: 2;
}

.card-header {
  background: rgba(13, 55, 73, 1);
  color: white;
  padding: 2.5rem 4.5rem 2.5rem 4.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.card-header::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4px;
  background: linear-gradient(
    90deg,
    #00997c 0%,
    #1abc9c 50%,
    rgba(0, 0, 0, 0) 100%
  );
}

.form-title {
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
}

.form-subtitle {
  font-size: 0.9rem;
  margin: 0;
  opacity: 0.95;
  color: #00dbb1; /* touche de vert dans la citation */
  font-weight: 400;
}

.card-body {
  padding: 2rem 2.25rem;
}

.email-input-container {
  margin-bottom: 1.25rem;
}

.button-container {
  display: flex;
  justify-content: center;
  margin-top: 0.75rem;
}

.send-button {
  font-size: 0.875rem;
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  min-width: 160px;
  background: #00997c;
  border: none;
  color: #ffffff;
  letter-spacing: 0;
  box-shadow: none;
  position: relative;
}

.send-button:hover:not(:disabled) {
  opacity: 0.9;
}

.send-button:active:not(:disabled) {
  opacity: 0.85;
}

.send-button:disabled {
  background: #e0e0e0;
  color: #9e9e9e;
  cursor: not-allowed;
  box-shadow: none;
}

.banner-container {
  margin-top: 1.5rem;
  max-width: 500px;
  width: 100%;
}

.register-footer {
  padding: 1.5rem 4vw;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modern-form-container {
    min-height: 100vh;
    padding: 1rem 1rem;
    justify-content: center;
  }

  .language-selector {
    top: 0.75rem;
    right: 0.75rem;
  }

  .language-dropdown-container {
    padding: 0.375rem 0.5rem;
    font-size: 0.8rem;
  }

  .logo-container {
    margin-bottom: 1rem;
  }

  .hit-logo {
    max-width: 200px;
  }

  .video-iframe {
    width: 100vw;
    height: 100vh;
    min-width: 100vw;
    min-height: 100vh;
  }

  .form-card {
    max-width: 100%;
    margin: 0 1rem;
    border-radius: 1rem;
  }

  .card-header {
    padding: 1.5rem 1.25rem;
  }

  .form-title {
    font-size: 1.25rem;
    margin: 0 0 0.25rem 0;
  }

  .form-subtitle {
    font-size: 0.8rem;
  }

  .card-body {
    padding: 1.5rem 1.25rem;
  }

  .email-input-container {
    margin-bottom: 1rem;
  }

  .button-container {
    margin-top: 0.625rem;
  }

  .send-button {
    padding: 0.875rem 1.5rem;
    min-width: 120px;
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .modern-form-container {
    padding: 0.75rem 0.5rem;
    justify-content: center;
  }

  .language-selector {
    top: 0.5rem;
    right: 0.5rem;
  }

  .language-dropdown-container {
    padding: 0.25rem 0.375rem;
    font-size: 0.75rem;
  }

  .logo-container {
    margin-bottom: 0.75rem;
  }

  .hit-logo {
    max-width: 160px;
  }

  .form-card {
    margin: 0 1.25rem;
    border-radius: 0.875rem;
    max-width: calc(100% - 2.5rem);
  }

  .card-header {
    padding: 1.25rem 1rem;
  }

  .form-title {
    font-size: 1.125rem;
    margin: 0 0 0.25rem 0;
  }

  .form-subtitle {
    font-size: 0.75rem;
  }

  .card-body {
    padding: 1.25rem 1rem;
  }

  .email-input-container {
    margin-bottom: 0.875rem;
  }

  .button-container {
    margin-top: 0.5rem;
  }

  .send-button {
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
  }
}

/* Extra small smartphones */
@media (max-width: 360px) {
  .form-card {
    margin: 0 1.5rem;
    max-width: calc(100% - 3rem);
  }

  .card-header {
    padding: 1rem 0.875rem;
  }

  .card-body {
    padding: 1rem 0.875rem;
  }

  .hit-logo {
    max-width: 140px;
  }
}

/* Animation for card entrance */
.form-card {
  animation: slideInUp 0.6s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Focus states and accessibility */
.send-button:focus {
  outline: 2px solid #00997c;
  outline-offset: 2px;
}

/* Desktop positioning - slightly higher */
@media (min-width: 769px) {
  .modern-form-container {
    justify-content: flex-start;
    padding: 12vh 1rem 2rem 1rem;
  }
}

/* Ensure video covers the entire screen properly on all devices */
@media (max-aspect-ratio: 16/9) {
  .video-iframe {
    width: 177.78vh;
    height: 100vh;
    min-width: 177.78vh;
    min-height: 100vh;
  }
}

@media (min-aspect-ratio: 16/9) {
  .video-iframe {
    width: 100vw;
    height: 56.25vw;
    min-width: 100vw;
    min-height: 56.25vw;
  }
}
</style>
